import { useState, useEffect } from 'react';
import { Calendar as CalendarIcon, Users, Building2, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EmployeeCalendar = ({ user }) => {
  const navigate = useNavigate();
  const [view, setView] = useState('department'); // 'department' or 'company'
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [approvedTimeOff, setApprovedTimeOff] = useState([]);
  const [loading, setLoading] = useState(true);

  const monthNames = ["January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const [selectedAbsence, setSelectedAbsence] = useState(null);
  const [showAbsenceDialog, setShowAbsenceDialog] = useState(false);
  const [showDayAbsencesDialog, setShowDayAbsencesDialog] = useState(false);
  const [selectedDayAbsences, setSelectedDayAbsences] = useState([]);
  const [selectedDayDate, setSelectedDayDate] = useState('');

  const fetchApprovedTimeOff = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const scope = view === 'department' ? 'department' : 'company';
      // Fetch all absences (approved, pending, cancelled) not just approved
      const response = await fetch(`${BACKEND_URL}/api/absences/calendar?scope=${scope}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch absence data');
      }

      const data = await response.json();
      setApprovedTimeOff(data);
    } catch (error) {
      toast.error('Failed to load calendar data');
    } finally {
      setLoading(false);
    }
  };

  const handleAbsenceClick = (absence) => {
    setSelectedAbsence(absence);
    setShowAbsenceDialog(true);
  };

  const handleViewAllAbsences = (day, absences) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const dateObj = new Date(dateStr);
    setSelectedDayDate(dateObj.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }));
    setSelectedDayAbsences(absences);
    setShowDayAbsencesDialog(true);
  };

  useEffect(() => {
    fetchApprovedTimeOff();
  }, [view]);

  const getTimeOffForDay = (day) => {
    if (!day) return [];
    
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const date = new Date(dateStr);
    const dayOfWeek = date.getDay(); // 0 = Sunday, 6 = Saturday
    
    return approvedTimeOff.filter(timeOff => {
      // Handle both old format (first_day/last_day) and new format (start_date/end_date)
      const startField = timeOff.start_date || timeOff.first_day;
      const endField = timeOff.end_date || timeOff.last_day;
      
      if (!startField || !endField) return false;
      
      const start = new Date(startField);
      const end = new Date(endField);
      
      // Check if date is in range
      const isInRange = date >= start && date <= end;
      if (!isInRange) return false;
      
      // If it's a weekend (Saturday or Sunday)
      if (dayOfWeek === 0 || dayOfWeek === 6) {
        // Only show if the weekend date was specifically selected as start or end
        const dateOnly = dateStr;
        const startOnly = startField.split('T')[0];
        const endOnly = endField.split('T')[0];
        return dateOnly === startOnly || dateOnly === endOnly;
      }
      
      // Weekday - show if in range
      return true;
    });
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    return { daysInMonth, startingDayOfWeek, year, month };
  };

  const { daysInMonth, startingDayOfWeek, year, month } = getDaysInMonth(currentMonth);

  const previousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };

  const days = [];
  for (let i = 0; i < startingDayOfWeek; i++) {
    days.push(null);
  }
  for (let i = 1; i <= daysInMonth; i++) {
    days.push(i);
  }

  return (
    <section className="space-y-6">
      {/* Header */}
      <div className="w-full bg-white rounded-3xl shadow-lg p-4 sm:p-6">
        <div className="flex flex-col gap-4">
          <Button onClick={() => navigate('/profile')} variant="ghost" size="sm" className="self-start">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Team Calendar</h2>
            <p className="text-sm text-gray-500 mt-1">View team absences and time off</p>
          </div>

          {/* View Toggle */}
          <div className="flex items-center gap-2 bg-gray-50 rounded-lg p-1 w-full sm:w-auto">
            <Button
              onClick={() => setView('department')}
              className={`flex items-center justify-center gap-2 flex-1 sm:flex-initial text-sm ${
                view === 'department'
                  ? 'bg-teal-600 text-white hover:bg-teal-700'
                  : 'bg-transparent text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Users className="w-4 h-4" />
              My Department
            </Button>
            <Button
              onClick={() => setView('company')}
              className={`flex items-center justify-center gap-2 flex-1 sm:flex-initial text-sm ${
                view === 'company'
                  ? 'bg-teal-600 text-white hover:bg-teal-700'
                  : 'bg-transparent text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Building2 className="w-4 h-4" />
              Company
            </Button>
          </div>
        </div>
      </div>

      {/* Calendar Card */}
      <div className="w-full bg-white rounded-3xl shadow-lg p-4 sm:p-6">
        {/* Calendar Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <h3 className="text-xl sm:text-2xl font-bold text-gray-800">
            {monthNames[month]} {year}
          </h3>
          <div className="flex items-center gap-2">
            <Button
              onClick={previousMonth}
              variant="outline"
              className="border-teal-500 text-teal-600 hover:bg-teal-50 text-xs sm:text-sm flex-1 sm:flex-initial"
            >
              <span className="hidden sm:inline">← Previous</span>
              <span className="sm:hidden">←</span>
            </Button>
            <Button
              onClick={nextMonth}
              variant="outline"
              className="border-teal-500 text-teal-600 hover:bg-teal-50 text-xs sm:text-sm flex-1 sm:flex-initial"
            >
              <span className="hidden sm:inline">Next →</span>
              <span className="sm:hidden">→</span>
            </Button>
          </div>
        </div>

        {/* Day Headers */}
        <div className="grid grid-cols-7 gap-1 sm:gap-2 mb-2 sm:mb-4">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, idx) => (
            <div key={day} className="text-center font-semibold text-gray-600 py-2 text-xs sm:text-sm">
              <span className="hidden sm:inline">{day}</span>
              <span className="sm:hidden">{day.substring(0, 1)}</span>
            </div>
          ))}
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7 gap-1 sm:gap-2">
          {days.map((day, index) => {
            const timeOffList = getTimeOffForDay(day);
            
            return (
              <div
                key={index}
                className={`min-h-[60px] sm:min-h-[100px] p-1 sm:p-3 rounded-lg border ${
                  day
                    ? 'bg-white border-gray-200 hover:border-teal-400 hover:shadow-md cursor-pointer transition-all'
                    : 'bg-gray-50 border-transparent'
                }`}
              >
                {day && (
                  <>
                    <div className="font-semibold text-gray-800 mb-1 sm:mb-2 text-xs sm:text-sm">{day}</div>
                    {/* Dynamic absences */}
                    <div className="space-y-0.5 sm:space-y-1">
                      {timeOffList.slice(0, 3).map((timeOff, idx) => {
                        // Color code by type
                        let bgColor = 'bg-gray-100 text-gray-700';
                        let title = `${timeOff.employee_name}`;
                        
                        if (timeOff.type === 'holiday') {
                          // Holiday colors based on status
                          if (timeOff.status === 'approved') {
                            bgColor = 'bg-blue-100 text-blue-700';
                          } else if (timeOff.status === 'pending') {
                            bgColor = 'bg-amber-100 text-amber-700';
                          }
                          title += ` - Holiday (${timeOff.total_days} days, ${timeOff.status})`;
                        } else if (timeOff.type === 'sickness') {
                          // Sickness colors by category
                          if (timeOff.absence_category === 'Sick') {
                            bgColor = 'bg-pink-100 text-pink-700';
                          } else if (timeOff.absence_category === 'Other') {
                            bgColor = 'bg-purple-100 text-purple-700';
                          } else {
                            bgColor = 'bg-red-100 text-red-700';
                          }
                          title += ` - ${timeOff.absence_type_name || 'Absence'} (${timeOff.total_days} days)`;
                        }
                        
                        return (
                          <button
                            key={idx}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAbsenceClick(timeOff);
                            }}
                            className={`w-full text-[10px] sm:text-xs px-1 sm:px-2 py-0.5 sm:py-1 rounded truncate text-left hover:opacity-80 transition-opacity ${bgColor}`}
                            title={title}
                          >
                            {timeOff.employee_name}
                          </button>
                        );
                      })}
                      {timeOffList.length > 3 && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewAllAbsences(day, timeOffList);
                          }}
                          className="w-full text-[10px] sm:text-xs px-1 sm:px-2 py-0.5 sm:py-1 rounded bg-gray-200 text-gray-700 hover:bg-gray-300 transition-colors text-left font-medium"
                          title={`View all ${timeOffList.length} absences`}
                        >
                          View {timeOffList.length - 3} more
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Legend */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <h4 className="text-lg font-bold text-gray-800 mb-4">Status Legend</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-blue-500 rounded"></div>
            <span className="text-sm text-gray-700">Approved</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-amber-500 rounded"></div>
            <span className="text-sm text-gray-700">Pending</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-red-500 rounded"></div>
            <span className="text-sm text-gray-700">Sick</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-gray-500 rounded"></div>
            <span className="text-sm text-gray-700">Cancelled</span>
          </div>
        </div>
      </div>

      {/* All Absences for a Day Dialog - VIEW ONLY */}
      <Dialog open={showDayAbsencesDialog} onOpenChange={setShowDayAbsencesDialog}>
        <DialogContent className="sm:max-w-[700px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>All Absences - {selectedDayDate}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-3">
            {selectedDayAbsences.map((absence, index) => {
              // Color code by type
              let bgColor = 'bg-gray-100 border-gray-300';
              let textColor = 'text-gray-700';
              let statusColor = 'text-gray-600';
              
              if (absence.type === 'holiday') {
                if (absence.status === 'approved') {
                  bgColor = 'bg-blue-50 border-blue-200';
                  textColor = 'text-blue-900';
                  statusColor = 'text-blue-600';
                } else if (absence.status === 'pending') {
                  bgColor = 'bg-amber-50 border-amber-200';
                  textColor = 'text-amber-900';
                  statusColor = 'text-amber-600';
                }
              } else if (absence.type === 'sickness') {
                if (absence.absence_category === 'Sick') {
                  bgColor = 'bg-pink-50 border-pink-200';
                  textColor = 'text-pink-900';
                  statusColor = 'text-pink-600';
                } else if (absence.absence_category === 'Other') {
                  bgColor = 'bg-purple-50 border-purple-200';
                  textColor = 'text-purple-900';
                  statusColor = 'text-purple-600';
                } else {
                  bgColor = 'bg-red-50 border-red-200';
                  textColor = 'text-red-900';
                  statusColor = 'text-red-600';
                }
              }
              
              return (
                <div 
                  key={index}
                  className={`p-4 rounded-lg border-2 ${bgColor}`}
                >
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h4 className={`text-lg font-semibold ${textColor}`}>
                        {absence.employee_name}
                      </h4>
                      <p className="text-sm text-gray-600">{absence.employee_department}</p>
                    </div>
                    <div className="text-right">
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${bgColor} ${statusColor}`}>
                        {absence.type === 'holiday' ? `Holiday - ${absence.status}` : 'Off Sick'}
                      </span>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div>
                      <p className="text-xs text-gray-600 mb-1">From</p>
                      <p className="text-sm font-medium">
                        {new Date(absence.start_date || absence.first_day).toLocaleDateString("en-GB", {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric'
                        })}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600 mb-1">To</p>
                      <p className="text-sm font-medium">
                        {new Date(absence.end_date || absence.last_day).toLocaleDateString("en-GB", {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric'
                        })}
                      </p>
                    </div>
                  </div>
                  
                  <div className="mb-3">
                    <p className="text-xs text-gray-600 mb-1">Duration</p>
                    <p className="text-sm font-semibold">{absence.total_days} day{absence.total_days !== 1 ? 's' : ''}</p>
                  </div>
                  
                  {/* Only show notes for holidays, not sickness */}
                  {absence.type === 'holiday' && absence.notes && (
                    <div className="mb-3">
                      <p className="text-xs text-gray-600 mb-1">Notes</p>
                      <p className="text-sm italic">"{absence.notes}"</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          
          <DialogFooter>
            <Button
              onClick={() => setShowDayAbsencesDialog(false)}
              variant="outline"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Absence Details Dialog - VIEW ONLY (No Edit/Delete) */}
      <Dialog open={showAbsenceDialog} onOpenChange={setShowAbsenceDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>
              {selectedAbsence && selectedAbsence.type === 'sickness' 
                ? 'Off Sick'  // Simplified title for sickness
                : 'Time Off Details'}
            </DialogTitle>
          </DialogHeader>
          
          {selectedAbsence && (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-1">Employee</p>
                <p className="text-lg font-semibold text-gray-900">{selectedAbsence.employee_name}</p>
                <p className="text-sm text-gray-600">{selectedAbsence.employee_department}</p>
              </div>

              {/* Show type for non-sickness or show generic for sickness */}
              {selectedAbsence.type === 'sickness' && (
                <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                  <p className="text-sm text-red-600 mb-1">Type</p>
                  <p className="text-lg font-semibold text-gray-900">Off Sick</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-blue-50 rounded-lg p-4">
                  <p className="text-xs text-blue-600 mb-1">From</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {new Date(selectedAbsence.start_date || selectedAbsence.first_day).toLocaleDateString("en-GB", { 
                      day: 'numeric', 
                      month: 'short', 
                      year: 'numeric' 
                    })}
                  </p>
                </div>
                <div className="bg-purple-50 rounded-lg p-4">
                  <p className="text-xs text-purple-600 mb-1">To</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {new Date(selectedAbsence.end_date || selectedAbsence.last_day).toLocaleDateString("en-GB", { 
                      day: 'numeric', 
                      month: 'short', 
                      year: 'numeric' 
                    })}
                  </p>
                </div>
              </div>

              <div className="bg-teal-50 rounded-lg p-4 border border-teal-200">
                <p className="text-sm text-teal-600 mb-1">Duration</p>
                <p className="text-2xl font-bold text-teal-900">{selectedAbsence.total_days} day{selectedAbsence.total_days !== 1 ? 's' : ''}</p>
              </div>

              {selectedAbsence.type === 'holiday' && selectedAbsence.status && (
                <div className={`rounded-lg p-4 ${
                  selectedAbsence.status === 'approved' ? 'bg-green-50 border border-green-200' :
                  selectedAbsence.status === 'pending' ? 'bg-amber-50 border border-amber-200' :
                  'bg-gray-50 border border-gray-200'
                }`}>
                  <p className="text-sm text-gray-600 mb-1">Status</p>
                  <p className="text-lg font-semibold capitalize">{selectedAbsence.status}</p>
                </div>
              )}

              {/* Only show notes for holidays, not for sickness */}
              {selectedAbsence.type === 'holiday' && selectedAbsence.notes && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600 mb-2">Notes</p>
                  <p className="text-gray-900 italic">"{selectedAbsence.notes}"</p>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button
              onClick={() => setShowAbsenceDialog(false)}
              variant="outline"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default EmployeeCalendar;
